import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, Gamepad2, History, Plane, Save, Settings, TrendingDown, TrendingUp, Trophy, XCircle } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const GAME_CODES = [
  { value: "1022", label: "1022 — Spribe (Aviator)" },
  { value: "1030", label: "1030 — AlbertAviator" },
  { value: "1016", label: "1016 — Jili" },
  { value: "1015", label: "1015 — SmartsoftGaming" },
];

// tab is driven by URL hash: #aviator | #casino | #settings
function getTab(location) {
  if (location.pathname.includes("/settings")) return "settings";
  if (location.hash === "#casino") return "casino";
  return "aviator";
}

function AdminCasino() {
  const location = useLocation();
  const navigate = useNavigate();
  const tab = getTab(location);

  const goTab = (t) => {
    if (t === "settings") navigate("/admin/casino/settings");
    else if (t === "casino") navigate("/admin/casino#casino");
    else navigate("/admin/casino");
  };

  return (
    <AdminLayout>
      <div style={page}>
        <div style={pageHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={iconWrap}><Plane size={20} color="#7c3aed" /></div>
            <div>
              <h1 style={title}>Casino</h1>
              <p style={subtitle}>Aviator & Casino game history, stats and settings</p>
            </div>
          </div>
        </div>

        <div style={tabRow}>
          <button type="button" style={tab === "aviator" ? activeTab : tabBtn} onClick={() => goTab("aviator")}>
            <Plane size={14} /> Aviator History
          </button>
          <button type="button" style={tab === "casino" ? activeTab : tabBtn} onClick={() => goTab("casino")}>
            <Gamepad2 size={14} /> Casino Games
          </button>
          <button type="button" style={tab === "settings" ? activeTab : tabBtn} onClick={() => goTab("settings")}>
            <Settings size={14} /> Settings
          </button>
        </div>

        {tab === "aviator"  && <GameHistory gameId={1030} label="Aviator" color="#dc2626" />}
        {tab === "casino"   && <GameHistory gameId={201}  label="Casino (Supernova)" color="#d4a017" />}
        {tab === "settings" && <CasinoSettings />}
      </div>
    </AdminLayout>
  );
}

/* ── Shared Game History (used for both Aviator and Casino tabs) ── */
function GameHistory({ gameId, label, color }) {
  const [stats, setStats]           = useState(null);
  const [topPlayers, setTopPlayers] = useState([]);
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [pg, setPg]                 = useState(1);
  const [loading, setLoading]       = useState(false);
  const [filters, setFilters]       = useState({ status: "", date_from: "", date_to: "", user_id: "" });

  const PAGE_SIZE = 50;

  const loadStats = useCallback(() => {
    API.get(`/admin/casino/stats?game_id=${gameId}`).then((r) => setStats(r.data)).catch(() => {});
    API.get(`/admin/casino/top-players?game_id=${gameId}`).then((r) => setTopPlayers(r.data.rows || [])).catch(() => {});
  }, [gameId]);

  const loadBets = useCallback(async (p = 1, f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: PAGE_SIZE, game_id: gameId });
      if (f.status)    params.append("status",    f.status);
      if (f.date_from) params.append("date_from", f.date_from);
      if (f.date_to)   params.append("date_to",   f.date_to);
      if (f.user_id)   params.append("user_id",   f.user_id);
      const res = await API.get(`/admin/casino/bets?${params}`);
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setPg(p);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [filters, gameId]);

  useEffect(() => { loadStats(); loadBets(1, filters); }, [gameId]); // eslint-disable-line

  const applyFilters = () => loadBets(1, filters);
  const clearFilters = () => { const f = { status: "", date_from: "", date_to: "", user_id: "" }; setFilters(f); loadBets(1, f); };
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={historyWrap}>

      {/* ── Stats grid ── */}
      {stats && (
        <div style={statsGrid}>
          <StatCard icon={<History size={18} color={color} />}       label="Total Bets"      value={stats.total_bets}              bg={`rgba(0,0,0,0.03)`} accent={color} />
          <StatCard icon={<TrendingUp size={18} color="#f97316" />}   label="Total Wagered"   value={`₹${fmt(stats.total_wagered)}`} bg="rgba(249,115,22,0.06)" accent="#f97316" />
          <StatCard icon={<TrendingDown size={18} color="#16a34a" />} label="Total Paid Out"  value={`₹${fmt(stats.total_paid)}`}   bg="rgba(22,163,74,0.06)"  accent="#16a34a" />
          <StatCard icon={<Trophy size={18} color="#312e81" />}       label="House Profit"    value={`₹${fmt(stats.house_profit)}`} bg="rgba(49,46,129,0.06)"  accent="#312e81"
            subValue={stats.house_profit >= 0 ? "▲ Profit" : "▼ Loss"} subColor={stats.house_profit >= 0 ? "#16a34a" : "#dc2626"} />
          <StatCard icon={<Clock size={18} color="#6b7280" />}        label="Today Bets"      value={stats.today_bets}              bg="rgba(107,114,128,0.06)" accent="#6b7280" />
          <StatCard icon={<TrendingUp size={18} color="#f97316" />}   label="Today Wagered"   value={`₹${fmt(stats.today_wagered)}`} bg="rgba(249,115,22,0.06)" accent="#f97316" />
          <StatCard icon={<TrendingDown size={18} color="#16a34a" />} label="Today Paid"      value={`₹${fmt(stats.today_paid)}`}   bg="rgba(22,163,74,0.06)"  accent="#16a34a" />
          <StatCard icon={<Trophy size={18} color="#312e81" />}       label="Today Profit"    value={`₹${fmt(stats.today_profit)}`} bg="rgba(49,46,129,0.06)"  accent="#312e81"
            subValue={stats.today_profit >= 0 ? "▲ Profit" : "▼ Loss"} subColor={stats.today_profit >= 0 ? "#16a34a" : "#dc2626"} />
        </div>
      )}

      {/* ── Top Players ── */}
      {topPlayers.length > 0 && (
        <div style={section}>
          <div style={sectionHead}>
            <Trophy size={16} color={color} />
            <span style={{ ...sectionTitle, color }}>Top Players — {label}</span>
            <span style={sectionSub}>by total wagered (top 20)</span>
          </div>
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  {["#", "Player", "Phone", "Bets", "Wagered ₹", "Won ₹", "House P&L ₹"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topPlayers.map((p, i) => (
                  <tr key={p.user_id} style={tr}>
                    <td style={{ ...td, fontWeight: 900, color: i < 3 ? color : "#6b7280" }}>{i + 1}</td>
                    <td style={td}><span style={userName}>{p.user_name}</span></td>
                    <td style={{ ...td, color: "#94a3b8", fontSize: 12 }}>{p.user_phone}</td>
                    <td style={td}>{p.total_bets}</td>
                    <td style={{ ...td, fontWeight: 800, color: "#f97316" }}>₹{fmt(p.total_wagered)}</td>
                    <td style={{ ...td, fontWeight: 800, color: "#16a34a" }}>₹{fmt(p.total_won)}</td>
                    <td style={{ ...td, fontWeight: 800, color: Number(p.house_profit) >= 0 ? "#312e81" : "#dc2626" }}>
                      {Number(p.house_profit) >= 0 ? "+" : ""}₹{fmt(p.house_profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div style={section}>
        <div style={sectionHead}>
          <History size={16} color="#6b7280" />
          <span style={sectionTitle}>All Bets — {label}</span>
          <span style={sectionSub}>{total} records</span>
        </div>
        <div style={filterBar}>
          <select style={filterInput} value={filters.status} onChange={(e) => setFilters((c) => ({ ...c, status: e.target.value }))}>
            <option value="">All Status</option>
            <option value="placed">Placed</option>
            <option value="settled">Settled</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <input style={filterInput} type="date" value={filters.date_from} onChange={(e) => setFilters((c) => ({ ...c, date_from: e.target.value }))} />
          <input style={filterInput} type="date" value={filters.date_to}   onChange={(e) => setFilters((c) => ({ ...c, date_to:   e.target.value }))} />
          <input style={filterInput} type="number" value={filters.user_id} onChange={(e) => setFilters((c) => ({ ...c, user_id: e.target.value }))} placeholder="User ID" />
          <button type="button" style={filterBtn} onClick={applyFilters}>Search</button>
          <button type="button" style={filterClear} onClick={clearFilters}>Clear</button>
        </div>

        {/* ── Bets table ── */}
        <div style={tableWrap}>
          {loading ? (
            <div style={centerMsg}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={centerMsg}>No bets found</div>
          ) : (
            <table style={table}>
              <thead>
                <tr>
                  {["#ID", "Player", "Txn ID", "Game", "Runner / Odds", "Bet ₹", "Win ₹", "House P&L ₹", "Result", "Date"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const hp = Number(r.bet_amount) - Number(r.win_amount || 0);
                  const isSettled = r.status === "settled";
                  const isWon  = isSettled && Number(r.win_amount || 0) > 0;
                  const isLost = isSettled && Number(r.win_amount || 0) === 0;
                  return (
                    <tr key={r.id} style={tr}>
                      <td style={td}>{r.id}</td>
                      <td style={td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <span style={userName}>{r.user_name}</span>
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>{r.user_phone}</span>
                        </div>
                      </td>
                      <td style={{ ...td, fontSize: 11, color: "#6b7280", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis" }}>{r.transaction_id}</td>
                      <td style={{ ...td, fontSize: 11 }}>{r.tp_game_id}{r.table_code ? <><br /><span style={{ color: "#94a3b8" }}>{r.table_code}</span></> : null}</td>
                      <td style={{ ...td, fontWeight: 700 }}>
                        {r.runner_name || "—"}
                        {r.rate ? <span style={{ color: "#6b7280", fontWeight: 400 }}> @{Number(r.rate).toFixed(2)}x</span> : null}
                      </td>
                      <td style={{ ...td, fontWeight: 800, color: "#dc2626" }}>₹{fmt(r.bet_amount)}</td>
                      <td style={{ ...td, fontWeight: 800, color: "#16a34a" }}>₹{fmt(r.win_amount || 0)}</td>
                      <td style={{ ...td, fontWeight: 800, color: hp >= 0 ? "#312e81" : "#dc2626" }}>
                        {hp >= 0 ? "+" : ""}₹{fmt(hp)}
                      </td>
                      <td style={td}><ResultChip status={r.status} isWon={isWon} isLost={isLost} /></td>
                      <td style={{ ...td, fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
                        {r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={pagRow}>
            <span style={pagInfo}>Showing {((pg - 1) * PAGE_SIZE) + 1}–{Math.min(pg * PAGE_SIZE, total)} of {total}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button style={pagBtn} disabled={pg <= 1} onClick={() => loadBets(pg - 1)}>← Prev</button>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{pg} / {totalPages}</span>
              <button style={pagBtn} disabled={pg >= totalPages} onClick={() => loadBets(pg + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Casino Settings ── */
function CasinoSettings() {
  const [form, setForm] = useState({ vking_api_url: "", vking_agent_code: "", vking_tp_game_id: "1022", aviator_enabled: "1" });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  useEffect(() => {
    API.get("/admin/settings").then((res) => {
      const d = res.data || {};
      setForm((c) => ({ ...c, vking_api_url: d.vking_api_url || "", vking_agent_code: d.vking_agent_code || "", vking_tp_game_id: d.vking_tp_game_id || "1022", aviator_enabled: d.aviator_enabled || "1" }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { await API.post("/admin/settings", form); setSaved(true); setTimeout(() => setSaved(false), 2500); }
    catch (err) { alert(err?.response?.data?.message || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div style={settingsWrap}>
      <section style={card}>
        <div style={cardHead}>
          <div style={{ ...cardIcon, background: "rgba(124,58,237,0.1)" }}><Plane size={16} color="#7c3aed" /></div>
          <h3 style={cardTitle}>Vking Casino API</h3>
        </div>
        <p style={cardDesc}>
          Connect to Vking Casino. Webhook base URL to give Vking: <code style={codeChip}>{window.location.origin}/vking/</code>
        </p>
        <SettingField label="Vking API Base URL" placeholder="https://api.vkingcasino.com" value={form.vking_api_url} onChange={(v) => setForm((c) => ({ ...c, vking_api_url: v }))} hint="Without trailing slash" />
        <SettingField label="Agent Code" placeholder="e.g. P7kAWwQSC7RuD8ef" value={form.vking_agent_code} onChange={(v) => setForm((c) => ({ ...c, vking_agent_code: v }))} hint="Keep this secret." password />
        <label style={fieldWrap}>
          <span style={fieldLabel}>Game / Provider</span>
          <select style={selectInput} value={form.vking_tp_game_id} onChange={(e) => setForm((c) => ({ ...c, vking_tp_game_id: e.target.value }))}>
            {GAME_CODES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </label>
        <label style={fieldWrap}>
          <span style={fieldLabel}>Aviator Enabled</span>
          <div style={toggleRow}>
            <button type="button" style={form.aviator_enabled === "1" ? toggleOn : toggleOff} onClick={() => setForm((c) => ({ ...c, aviator_enabled: c.aviator_enabled === "1" ? "0" : "1" }))}>
              {form.aviator_enabled === "1" ? "ON" : "OFF"}
            </button>
            <span style={hint}>Show/hide Aviator card on user dashboard</span>
          </div>
        </label>
      </section>

      <section style={card}>
        <div style={cardHead}>
          <div style={{ ...cardIcon, background: "rgba(16,185,129,0.1)" }}><Settings size={16} color="#10b981" /></div>
          <h3 style={cardTitle}>Webhook URLs (give to Vking)</h3>
        </div>
        {[
          { label: "Validate User",  path: "/vking/validateuser" },
          { label: "Get Balance",    path: "/vking/getbalance" },
          { label: "Place Bet",      path: "/vking/placebet" },
          { label: "Settle Game",    path: "/vking/settlegame" },
          { label: "Cancel Bet",     path: "/vking/cancelbet" },
          { label: "Cancel Game",    path: "/vking/cancelgame" },
          { label: "Cancel Settled", path: "/vking/cancelsettledgame" },
          { label: "Resettle Game",  path: "/vking/resettlegame" },
        ].map((wh) => (
          <div key={wh.path} style={webhookRow}>
            <span style={webhookLabel}>{wh.label}</span>
            <code style={webhookUrl}>{window.location.origin.replace(":3000", ":3001")}{wh.path}</code>
          </div>
        ))}
      </section>

      <div style={saveRow}>
        {saved && <span style={savedMsg}><CheckCircle2 size={14} /> Saved!</span>}
        <button type="button" style={saveBtn} onClick={save} disabled={saving}>
          <Save size={15} /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

/* ── Small components ── */
function StatCard({ icon, label, value, bg, accent, subValue, subColor }) {
  return (
    <div style={{ ...statCard, background: bg, borderTop: `3px solid ${accent || "#e5e7eb"}` }}>
      <div style={statIcon}>{icon}</div>
      <div style={statVal}>{value}</div>
      <div style={statLabel}>{label}</div>
      {subValue && <div style={{ fontSize: 11, fontWeight: 800, color: subColor || "#6b7280", marginTop: 2 }}>{subValue}</div>}
    </div>
  );
}

function ResultChip({ status, isWon, isLost }) {
  if (status === "placed")    return <span style={{ ...chip, background: "#fef9c3", color: "#78350f" }}><Clock size={10} /> Pending</span>;
  if (status === "cancelled") return <span style={{ ...chip, background: "#f3f4f6", color: "#6b7280" }}><XCircle size={10} /> Cancelled</span>;
  if (isWon)  return <span style={{ ...chip, background: "#dcfce7", color: "#15803d" }}><TrendingDown size={10} /> Won</span>;
  if (isLost) return <span style={{ ...chip, background: "#fee2e2", color: "#dc2626" }}><TrendingUp size={10} /> Lost</span>;
  return <span style={{ ...chip, background: "#f3f4f6", color: "#6b7280" }}>—</span>;
}

function SettingField({ label, value, onChange, placeholder, hint, password }) {
  return (
    <label style={fieldWrap}>
      <span style={fieldLabel}>{label}</span>
      <input style={fieldInput} type={password ? "password" : "text"} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      {hint && <span style={hintStyle}>{hint}</span>}
    </label>
  );
}

function fmt(v) {
  const n = Number(v || 0);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000)   return `${(n / 100000).toFixed(2)}L`;
  if (n >= 1000)     return `${(n / 1000).toFixed(1)}K`;
  return n.toFixed(2);
}

/* ── Styles ── */
const page       = { padding: "24px 20px", maxWidth: 1200, margin: "0 auto" };
const pageHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 };
const iconWrap   = { width: 44, height: 44, borderRadius: 12, background: "rgba(124,58,237,0.1)", display: "grid", placeItems: "center", flexShrink: 0 };
const title      = { margin: 0, fontSize: 22, fontWeight: 900, color: "#0f172a" };
const subtitle   = { margin: "3px 0 0", fontSize: 13, color: "#64748b" };

const tabRow     = { display: "flex", gap: 8, marginBottom: 20, borderBottom: "2px solid #e5e7eb" };
const tabBtn     = { height: 40, padding: "0 18px", border: 0, background: "transparent", color: "#64748b", fontWeight: 700, fontSize: 13, cursor: "pointer", borderBottom: "2px solid transparent", marginBottom: -2, display: "flex", alignItems: "center", gap: 7 };
const activeTab  = { ...tabBtn, color: "#312e81", borderBottomColor: "#312e81" };

const historyWrap = { display: "grid", gap: 20 };
const statsGrid   = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: 12 };
const statCard    = { borderRadius: 12, padding: "14px 16px", border: "1px solid #e5e7eb", display: "flex", flexDirection: "column", gap: 5 };
const statIcon    = { display: "flex" };
const statVal     = { fontSize: 20, fontWeight: 900, color: "#0f172a" };
const statLabel   = { fontSize: 11, fontWeight: 700, color: "#6b7280" };

const section     = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" };
const sectionHead = { display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: "1px solid #f3f4f6" };
const sectionTitle= { fontSize: 15, fontWeight: 800, color: "#0f172a" };
const sectionSub  = { fontSize: 12, color: "#94a3b8", marginLeft: "auto" };

const filterBar   = { display: "flex", gap: 8, flexWrap: "wrap", padding: "12px 14px", borderBottom: "1px solid #f3f4f6" };
const filterInput = { height: 36, padding: "0 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, color: "#0f172a", outline: "none", minWidth: 110 };
const filterBtn   = { height: 36, padding: "0 18px", border: 0, borderRadius: 8, background: "#312e81", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" };
const filterClear = { ...filterBtn, background: "#e5e7eb", color: "#374151" };

const tableWrap   = { overflowX: "auto" };
const table       = { width: "100%", borderCollapse: "collapse", minWidth: 820 };
const th          = { padding: "11px 14px", background: "#f8fafc", borderBottom: "2px solid #e5e7eb", textAlign: "left", fontSize: 11, fontWeight: 800, color: "#6b7280", whiteSpace: "nowrap" };
const tr          = { borderBottom: "1px solid #f3f4f6" };
const td          = { padding: "10px 14px", fontSize: 13, color: "#374151", verticalAlign: "middle" };
const userName    = { fontSize: 13, fontWeight: 700, color: "#0f172a" };
const chip        = { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 800 };
const centerMsg   = { textAlign: "center", padding: 36, color: "#94a3b8", fontSize: 14 };

const pagRow  = { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, padding: "12px 16px", borderTop: "1px solid #f3f4f6" };
const pagInfo = { fontSize: 13, color: "#6b7280" };
const pagBtn  = { height: 32, padding: "0 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", color: "#374151", fontWeight: 700, fontSize: 13, cursor: "pointer" };

// Settings styles
const settingsWrap = { display: "grid", gap: 18 };
const card        = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "20px 20px 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" };
const cardHead    = { display: "flex", alignItems: "center", gap: 12, marginBottom: 14 };
const cardIcon    = { width: 36, height: 36, borderRadius: 10, display: "grid", placeItems: "center", flexShrink: 0 };
const cardTitle   = { margin: 0, fontSize: 16, fontWeight: 800, color: "#0f172a" };
const cardDesc    = { margin: "0 0 14px", fontSize: 13, color: "#64748b", lineHeight: 1.6 };
const fieldWrap   = { display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 };
const fieldLabel  = { fontSize: 12, fontWeight: 800, color: "#374151" };
const fieldInput  = { height: 42, padding: "0 12px", border: "1.5px solid #d1d5db", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#0f172a", outline: "none", background: "#fff" };
const selectInput = { height: 42, padding: "0 12px", border: "1.5px solid #d1d5db", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#0f172a", outline: "none", background: "#fff" };
const hintStyle   = { fontSize: 11, color: "#9ca3af" };
const hint        = { fontSize: 11, color: "#9ca3af" };
const toggleRow   = { display: "flex", alignItems: "center", gap: 12 };
const toggleOn    = { height: 34, padding: "0 18px", border: 0, borderRadius: 999, background: "#16a34a", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" };
const toggleOff   = { ...toggleOn, background: "#e5e7eb", color: "#6b7280" };
const codeChip    = { background: "#f1f5f9", borderRadius: 6, padding: "2px 8px", fontSize: 12, fontWeight: 700, color: "#312e81", fontFamily: "monospace" };
const webhookRow  = { display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid #f3f4f6" };
const webhookLabel= { fontSize: 12, fontWeight: 700, color: "#374151", width: 130, flexShrink: 0 };
const webhookUrl  = { fontSize: 11, color: "#6b7280", fontFamily: "monospace", background: "#f8fafc", borderRadius: 6, padding: "3px 8px", flex: 1, overflow: "hidden", textOverflow: "ellipsis" };
const saveRow     = { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 14 };
const savedMsg    = { display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#16a34a" };
const saveBtn     = { height: 44, padding: "0 24px", border: 0, borderRadius: 12, background: "#312e81", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, boxShadow: "0 4px 14px rgba(49,46,129,0.28)" };

export default AdminCasino;
